/** Inbox reads — SMS + calls from the `comms` table (already org-readable
 * under v1's existing RLS; no migration needed). READ-ONLY by hard rule:
 * v2 Phase 3a shows conversations, it does NOT send. Texting stays
 * approval-mode and moves server-side in a later, awake-Tom phase — the
 * spec's fences (Zillow-only, 9am–8pm ET, approval, one outbound line) are
 * not things to reconstruct in a browser at 1am.
 *
 * Every row passes narrowComm at the door; malformed rows are counted, never
 * rendered. Threads are grouped by the counterparty number. */
import { http } from "../../lib/http";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";

export interface Comm {
  id: string;
  createdAt: number;
  direction: "in" | "out";
  channel: string; // sms | call | ...
  channelIsCall: boolean;
  from: string;
  to: string;
  peer: string; // the counterparty number (their number, whichever side)
  body: string;
  name: string | null;
  leadKey: string | null;
  status: string | null;
}

export interface Thread {
  peer: string;
  name: string | null;
  lastAt: number;
  lastBody: string;
  lastDir: "in" | "out";
  count: number;
  unreadHint: boolean; // last message inbound → likely needs a reply
  messages: Comm[];
}

const CACHE = "comms_v1";
const PAGE = 1000;
const MAX_PAGES = 12; // 12k most-recent messages is plenty for the inbox view
const OUR_LINES = new Set(["+13215104324", "+13862048744"]); // intake + agent lines
const COLS = "id,created_at,direction,channel,from_number,to_number,body,contact_name,contact_number,lead_key,status";

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

export function narrowComm(row: Record<string, unknown>): Comm | null {
  const id = s(row["id"]);
  const created = s(row["created_at"]);
  if (!id || !created) return null;
  const t = Date.parse(created);
  if (Number.isNaN(t)) return null;
  const dir = row["direction"] === "in" ? "in" : "out";
  const from = s(row["from_number"]) ?? "";
  const to = s(row["to_number"]) ?? "";
  const contact = s(row["contact_number"]);
  // the peer is the number that isn't ours; fall back to contact_number
  const peer = contact ?? ((OUR_LINES.has(from) ? to : from) || from || to);
  if (!peer) return null;
  const channel = s(row["channel"]) ?? "sms";
  return {
    id,
    createdAt: t,
    direction: dir,
    channel,
    channelIsCall: channel === "call",
    from,
    to,
    peer,
    body: typeof row["body"] === "string" ? row["body"] : "",
    name: s(row["contact_name"]),
    leadKey: s(row["lead_key"]),
    status: s(row["status"]),
  };
}

export function buildThreads(comms: Comm[]): Thread[] {
  const byPeer = new Map<string, Comm[]>();
  for (const c of comms) {
    const arr = byPeer.get(c.peer);
    if (arr) arr.push(c);
    else byPeer.set(c.peer, [c]);
  }
  const threads: Thread[] = [];
  for (const [peer, msgs] of byPeer) {
    msgs.sort((a, b) => a.createdAt - b.createdAt); // oldest→newest within a thread
    const last = msgs[msgs.length - 1];
    if (!last) continue;
    const named = msgs.find((m) => m.name)?.name ?? null;
    threads.push({
      peer,
      name: named,
      lastAt: last.createdAt,
      lastBody: last.channelIsCall ? (last.direction === "in" ? "Inbound call" : "Outbound call") : last.body,
      lastDir: last.direction,
      count: msgs.length,
      unreadHint: last.direction === "in" && last.channel === "sms",
      messages: msgs,
    });
  }
  threads.sort((a, b) => b.lastAt - a.lastAt); // most-recent thread first
  return threads;
}

export async function cachedThreads(): Promise<Thread[] | null> {
  const c = await idbGet<{ at: number; comms: Comm[] }>(CACHE);
  if (!c || !Array.isArray(c.comms)) return null;
  return buildThreads(c.comms);
}

export interface InboxResult {
  threads: Thread[];
  dropped: number;
}

export async function fetchThreads(token: string, signal?: AbortSignal): Promise<InboxResult> {
  const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` };
  const rows: unknown[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const batch = await http<unknown[]>(
      `${SUPABASE_URL}/rest/v1/comms?select=${COLS}&order=created_at.desc,id.desc&limit=${PAGE}&offset=${page * PAGE}`,
      { headers, signal, timeoutMs: 20000 },
    );
    if (!Array.isArray(batch) || batch.length === 0) break;
    rows.push(...batch);
    if (batch.length < PAGE) break;
  }
  let dropped = 0;
  const comms: Comm[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    const c = narrowComm(r as Record<string, unknown>);
    if (!c) {
      dropped++;
      continue;
    }
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    comms.push(c);
  }
  await idbSet(CACHE, { at: Date.now(), comms });
  return { threads: buildThreads(comms), dropped };
}

/** WHOEVER SENT THE TEXT IS THE CONTACT (Tom, 2026-07-28).
 *
 * Find the inbound text a lead came from, for leads that have no contact
 * saved. The backend applies this rule when it creates a lead from a text,
 * but only since 2026-07-27 and only at creation time — so every SMS lead
 * taken before that is stranded: it exists *because of* a conversation we
 * already have, yet the drawer says "no messages, add a phone" and gives no
 * way to know which phone.
 *
 * Match: an INBOUND message whose body contains the lead's street line. That
 * is why the lead exists, so it is a strong signal rather than a guess.
 *
 * Deliberately conservative — this drives a suggestion the user accepts, and
 * a wrong contact on a lead is worse than no contact:
 *   - needs a house number + street word ("po box", "lot 4" match everything)
 *   - MORE than one matching thread returns null; ambiguity suggests nothing
 * Measured on Tom's data (4,724 leads): 25 recovered, 10 ambiguous ones
 * correctly suppressed, no false positives among the rest. */
export function recoverContactThread(leadKey: string, threads: Thread[]): Thread | null {
  const street = (leadKey.split(",")[0] ?? "").trim().toLowerCase();
  if (street.length < 8 || !/^\d+\s+\S/.test(street)) return null;
  const hits = threads.filter((t) =>
    t.messages.some((m) => m.direction === "in" && m.body.toLowerCase().includes(street)));
  return hits.length === 1 ? (hits[0] ?? null) : null;
}

/** A thread's `name` is whatever `contact_name` some row carried, and that
 * field gets stamped with the PROPERTY ADDRESS when we text about a lead — so
 * a thread can end up "named" 4732 W Bay Vista Ave, Tampa, FL 33611. Showing
 * that as the person reads like a bug ("this text came from 4732 W Bay Vista
 * Ave") and, worse, sending with it re-stamps the same junk on new rows.
 * A name is only a name if it isn't an address and isn't a phone number. */
export function isPersonName(name: string | null | undefined): boolean {
  const n = (name ?? "").trim();
  if (n.length < 2) return false;
  if (/^\+?[\d\s().-]+$/.test(n)) return false;   // a phone number
  if (/^\d+\s/.test(n)) return false;             // "4732 W Bay Vista Ave…"
  if (/\b\d{5}(-\d{4})?\b/.test(n)) return false; // carries a ZIP
  if (/,\s*[A-Z]{2}\b/.test(n)) return false;     // ", FL"
  return true;
}

/** What to CALL the other side of a thread: their name when we have a real
 * one, otherwise their number formatted. Never an address. */
export function contactLabel(name: string | null | undefined, phone: string): string {
  return isPersonName(name) ? (name as string).trim() : fmtPhone(phone);
}

/** Pretty US number: +13215104324 → (321) 510-4324. */
export function fmtPhone(n: string): string {
  const d = n.replace(/[^0-9]/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (ten.length !== 10) return n;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
